import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  Autocomplete,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

function ChartControls({
  historyData,
  selectedIndicators,
  setSelectedIndicators,
  historyTimeRange,
  setHistoryTimeRange,
}) {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const allIndicators = Object.keys(historyData);
  const selectedCount = selectedIndicators.length;
  
  const handleToggleIndicator = (indicatorId) => {
    if (selectedIndicators.includes(indicatorId)) {
      setSelectedIndicators(selectedIndicators.filter(id => id !== indicatorId));
    } else {
      setSelectedIndicators([...selectedIndicators, indicatorId]);
    }
  };
  
  return (
    <>
      {/* Filter Button & Time Range */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: 2,
        flexWrap: 'wrap',
      }}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            px: 3,
            py: 1.5,
            fontWeight: 600,
            borderColor: selectedCount > 0 ? '#667eea' : 'divider',
            color: selectedCount > 0 ? '#667eea' : 'text.primary',
            bgcolor: selectedCount > 0 ? alpha('#667eea', 0.05) : 'transparent',
            '&:hover': {
              borderColor: '#667eea',
              bgcolor: alpha('#667eea', 0.1),
            },
          }}
        >
          {selectedCount > 0 ? `${selectedCount} Indicator${selectedCount > 1 ? 's' : ''} Selected` : 'Select Indicators'}
        </Button>
        
        {/* Time Range - Right aligned */}
        <ToggleButtonGroup
          value={historyTimeRange}
          exclusive
          onChange={(e, newRange) => newRange && setHistoryTimeRange(newRange)}
          size="small"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            ml: 'auto',
            '& .MuiToggleButton-root': {
              px: 2.5,
              py: 1,
              fontWeight: 600,
              border: 'none',
              color: 'text.secondary',
              '&.Mui-selected': {
                bgcolor: '#667eea',
                color: 'white',
                '&:hover': {
                  bgcolor: '#5568d3',
                },
              },
              '&:hover': {
                bgcolor: alpha('#667eea', 0.08),
              },
            },
          }}
        >
          <ToggleButton value="3M">3M</ToggleButton>
          <ToggleButton value="6M">6M</ToggleButton>
          <ToggleButton value="1Y">1Y</ToggleButton>
          <ToggleButton value="2Y">2Y</ToggleButton>
          <ToggleButton value="ALL">ALL</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      {/* Filter Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FilterListIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('observations.selectIndicators', 'Select Indicators to Display')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpenDialog(false)}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: alpha('#667eea', 0.08),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            {/* Autocomplete */}
            <Box sx={{ mb: 3 }}>
              <Autocomplete
                multiple
                size="small"
                options={allIndicators}
                value={selectedIndicators}
                onChange={(e, newValue) => setSelectedIndicators(newValue)}
                disableCloseOnSelect
                getOptionLabel={(option) => option}
                renderOption={(props, option, { selected }) => (
                  <Box 
                    component="li" 
                    {...props}
                    sx={{
                      py: 1,
                      px: 2,
                      '&:hover': {
                        bgcolor: alpha('#667eea', 0.08),
                      },
                    }}
                  >
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                      sx={{ mr: 1.5 }}
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontWeight: selected ? 600 : 400,
                        color: selected ? '#667eea' : 'text.primary',
                      }}
                    >
                      {option}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder={t('observations.clickToSelect', 'Click to select...')}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: 42,
                      },
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                    {value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                        sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          bgcolor: alpha('#667eea', 0.1),
                          color: '#667eea',
                          border: `1px solid ${alpha('#667eea', 0.2)}`,
                          '& .MuiChip-deleteIcon': {
                            color: '#667eea',
                            '&:hover': {
                              color: '#5568d3',
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
              />
            </Box>
            
            {/* Quick Select Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => setSelectedIndicators(Object.keys(historyData).slice(0, 6))}
                sx={{
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: '#667eea',
                    bgcolor: alpha('#667eea', 0.05),
                  },
                }}
              >
                Top 6
              </Button>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => setSelectedIndicators(Object.keys(historyData))}
                sx={{
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: '#667eea',
                    bgcolor: alpha('#667eea', 0.05),
                  },
                }}
              >
                {t('observations.all', 'All')}
              </Button>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => setSelectedIndicators([])}
                sx={{
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: '#ef4444',
                    bgcolor: alpha('#ef4444', 0.05),
                  },
                }}
              >
                {t('observations.clear', 'Clear')}
              </Button>
            </Box>
            
            {/* Selected List */}
            {selectedIndicators.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
                  Selected ({selectedIndicators.length})
                </Typography>
                <Box sx={{ 
                  maxHeight: 300, 
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}>
                  <List dense sx={{ p: 0 }}>
                    {selectedIndicators.map((indicatorId) => (
                      <ListItem
                        key={indicatorId}
                        disablePadding
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleToggleIndicator(indicatorId)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                bgcolor: alpha('#ef4444', 0.08),
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemButton
                          onClick={() => handleToggleIndicator(indicatorId)}
                          sx={{
                            py: 1,
                            px: 2,
                            '&:hover': {
                              bgcolor: alpha('#667eea', 0.05),
                            },
                          }}
                        >
                          <CheckCircleIcon 
                            sx={{ 
                              mr: 1.5, 
                              color: '#667eea',
                              fontSize: 18,
                            }} 
                          />
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontWeight: 600,
                                  color: 'text.primary',
                                }}
                              >
                                {indicatorId}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ p: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              onClick={() => setSelectedIndicators([])}
              color="error"
              size="medium"
              sx={{ fontWeight: 600 }}
            >
              Clear All
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => setOpenDialog(false)}
              variant="contained"
              size="medium"
              sx={{
                fontWeight: 600,
                bgcolor: '#667eea',
                px: 3,
                '&:hover': {
                  bgcolor: '#5568d3',
                },
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );
}

export default ChartControls;
